<template>
    <b-row>
        <b-col
            v-for="(bucket, colIndex) in columns"
            :key="colIndex"
            cols="12"
            :md="bootstrapColSpan"
        >
            <slot v-for="item in bucket" :item="item"/>
        </b-col>
    </b-row>
</template>

<script>
/**
 * Renders a list of items into N Bootstrap columns using column-first ordering,
 * so a pre-sorted array reads alphabetically top-to-bottom within each column
 * instead of left-to-right across rows.
 *
 * Usage:
 *   <card-column-list :items="sortedItems"
 *                     :cols="{ sm: 1, md: 2, lg: 3 }"
 *                     v-slot="{ item }">
 *       <your-card :key="item.id" :item="item"/>
 *   </card-column-list>
 */
export default {
    name: 'CardColumnList',
    props: {
        items: {
            type: Array,
            required: true,
        },
        // desired number of columns per vue-mq breakpoint
        cols: {
            type: Object,
            default: () => ({ sm: 1, md: 2, lg: 3, xl: 3 }),
        },
    },
    computed: {
        activeColCount() {
            const bp = this.$mq
            const desired = this.cols[bp] ?? this.cols.lg ?? this.cols.md ?? 1
            // never render more columns than items (avoids empty trailing columns)
            return Math.max(1, Math.min(desired, this.items.length || 1))
        },
        bootstrapColSpan() {
            return Math.max(1, Math.floor(12 / this.activeColCount))
        },
        // split items into N column-first buckets
        // e.g. [A,B,C,D,E,F,G,H] with N=3  ->  [[A,B,C], [D,E,F], [G,H]]
        columns() {
            const n = this.activeColCount
            const perCol = Math.ceil(this.items.length / n)
            const buckets = []
            for (let c = 0; c < n; c++) {
                buckets.push(this.items.slice(c * perCol, (c + 1) * perCol))
            }
            return buckets
        },
    },
}
</script>
